import { FolderIconClosed } from '@/utils/icons';
import { PendingSpinner } from '../PendingSpinner/PendingSpinner';
import type { INewFolder } from '../hooks/useNewFolder';
import styles from './FolderNav.module.css';
import { isImeComposing } from '@utils/ime';

interface INewFolderNodeProps {
  newFolder: INewFolder;
  depth: number;
  value: string;
  error: string | null;
  onChange: (value: string) => void;
  onCommit: () => void;
  onCancel: () => void;
}

/**
 * The folder being created, drawn in the tree but not a TreeNode: with no id
 * there is nothing to select, drag, drop onto or right-click with, so none of
 * those handlers exist here rather than being disabled.
 */
export function NewFolderNode({ newFolder, depth, value, error, onChange, onCommit, onCancel }: INewFolderNodeProps) {
  const indentStyle = { paddingLeft: `calc(var(--s-3) + ${depth * 16}px)` };

  if (newFolder.status === 'creating') {
    return (
      <div className={styles.item} style={indentStyle} aria-busy="true">
        <span className={styles.chevronWrap} />
        <span className={styles.folderIcon} aria-hidden="true"><FolderIconClosed size={16} /></span>
        <span className={styles.label}>{newFolder.name}</span>
        <PendingSpinner />
      </div>
    );
  }

  return (
    <div className={styles.item} style={indentStyle}>
      <span className={styles.chevronWrap} />
      <span className={styles.folderIcon} aria-hidden="true"><FolderIconClosed size={16} /></span>
      <span className={styles.renameField}>
        <input
          className={`${styles.renameInput} ${error ? styles.renameInputError : ''}`}
          value={value}
          autoFocus
          aria-invalid={error ? true : undefined}
          onFocus={(e) => e.target.select()}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onCommit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !isImeComposing(e)) { e.preventDefault(); onCommit(); }
            if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
          }}
          onClick={(e) => e.stopPropagation()}
        />
        {error && <span className={styles.renameError} role="alert">{error}</span>}
      </span>
    </div>
  );
}
